import React, { useState } from 'react';
import NumberFormat from 'react-number-format';

import { makeStyles } from '@material-ui/core/styles';

import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';
import MenuItem from '@material-ui/core/MenuItem';

import States from 'app/utils/States';
import TimeZones from 'app/utils/TimeZones';

import { paper } from 'app/styles/core';

import useApp from 'app/context/useApp';
import useApi from 'app/context/useApi';

const useStyles = makeStyles(theme => ({
    paper: {
        ...paper(theme),
        marginBottom: theme.spacing(3)
    },
    marginTop: {
        marginTop: theme.spacing(3)
    },
    divider: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(3)
    },
    saveButtonContainer: {
        marginTop: theme.spacing(3)
    },
    saveButton: {
        paddingLeft: theme.spacing(5),
        paddingRight: theme.spacing(5),
    },
}));

export default function General(props) {

    const { app } = useApp();
    const api = useApi();

    const [ club, setClub ] = useState({
        name: app.club.name,
        description: app.club.description,
        support_email: app.club.support_email,
        support_phone: app.club.support_phone || "",
        timezone: app.club.timezone,

        //Meta
        legal_name: app.club.meta.legal_name || "",
        legal_address_one: app.club.meta.legal_address_one || "",
        legal_address_two: app.club.meta.legal_address_two || "",
        legal_city: app.club.meta.legal_city || "",
        legal_state: app.club.meta.legal_state || "",
        legal_zip: app.club.meta.legal_zip || "",
    });

    const [ loading, setLoading ] = useState(false);
    const [ errors, setErrors ] = useState({});

    const classes = useStyles();

    const handleChange = name => event => {
        const target = event.target;
        const value = (target.type === 'checkbox' ? target.checked : target.value);

        if (isError(name)) delete errors[name];

        setClub({ ...club, [name]: value });
    };

    const isError = field => {
        if (errors[field]) return true;
        return false;
    }

    const getErrorMessage = field => {
        if (isError(field)) return errors[field][0];
        return "";
    }

    const postGeneral = () => {
        setLoading(true);

        const data = {
            name: club.name,
            legal_name: club.legal_name,
            description: club.description,
            support_email: club.support_email,
            support_phone: club.support_phone,
            timezone: club.timezone,
        }

        api.post('/admin/club/general', data)
            .catch(error => {
                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                }
            }).finally(() => setLoading(false));
    }

    const postAddress = () => {
        setLoading(true);

        const data = {
            legal_address_one: club.legal_address_one,
            legal_address_two: club.legal_address_two,
            legal_city: club.legal_city,
            legal_state: club.legal_state,
            legal_zip: club.legal_zip,
        }

        api.post('/admin/club/address', data)
            .catch(error => {
                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                }
            }).finally(() => setLoading(false));
    }

    return (
        <form noValidate autoComplete="off">
            <Paper className={classes.paper}>
                <Grid container
                    alignItems="center"
                    justify="space-between"
                    spacing={3}
                    >
                    <Grid item>
                        <Typography variant="h6" component="h2" gutterBottom>
                            General Settings
                        </Typography>
                    </Grid>
                </Grid>
                <Typography color="textSecondary">
                    Welcome to your club settings. Here you can edit club profile as well as your club address and legal entity information. You can also access your clubs branding, subscription and more using the club settings sidebar.
                </Typography>
            </Paper>
            <Paper className={classes.paper}>
                <Grid container
                    alignItems="center"
                    justify="space-between"
                    spacing={3}
                    >
                    <Grid item>
                        <Typography variant="h6" component="h3" gutterBottom>
                            Club Profile
                        </Typography>
                    </Grid>
                </Grid>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            id="input-club-name"
                            label="Club Name"
                            value={club.name}
                            onChange={handleChange('name')}
                            fullWidth
                            error={isError('name')}
                            helperText={getErrorMessage('name')}
                            variant='outlined'
                            required
                            margin="normal"
                        />
                        <TextField
                            id="input-club-description"
                            label="Club Description"
                            name="description"
                            value={club.description}
                            onChange={handleChange('description')}
                            fullWidth
                            error={isError('description')}
                            helperText={getErrorMessage('description')}
                            variant="outlined"
                            multiline
                            rows="5"
                            rowsMax="5"
                            inputProps={{
                                maxLength: "255"
                            }}
                            required
                            margin="normal"
                        />
                        <TextField
                            className={classes.marginTop}
                            id="input-club-timezone"
                            label="Club Timezone"
                            name="timezone"
                            value={club.timezone}
                            onChange={handleChange('timezone')}
                            fullWidth
                            error={isError('timezone')}
                            helperText={getErrorMessage('timezone')}
                            variant="outlined"
                            select
                            required
                            margin="normal"
                            >
                            {TimeZones.map(option => (
                                <MenuItem key={option.tz} value={option.tz}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            id="input-club-legal-name"
                            label="Legal Name"
                            value={club.legal_name}
                            onChange={handleChange('legal_name')}
                            fullWidth
                            error={isError('legal_name')}
                            helperText={getErrorMessage('legal_name')}
                            variant='outlined'
                            margin="normal"
                        />
                        <TextField
                            id="input-club-support-email"
                            label="Support Email"
                            value={club.support_email}
                            onChange={handleChange('support_email')}
                            fullWidth
                            error={isError('support_email')}
                            helperText={getErrorMessage('support_email')}
                            variant='outlined'
                            required
                            margin="normal"
                        />
                        <NumberFormat
                            format="(###) ###-####"
                            allowEmptyFormatting
                            customInput={TextField}
                            id="input-club-support-phone"
                            label="Support Phone"
                            value={club.support_phone}
                            onChange={handleChange('support_phone')}
                            fullWidth
                            type="tel"
                            error={isError('support_phone')}
                            helperText={getErrorMessage('support_phone')}
                            variant='outlined'
                            margin="normal"
                        />
                    </Grid>
                </Grid>
                <Typography align="right" component="div" className={classes.saveButtonContainer}>
                    <Button
                        disabled={loading}
                        variant="contained"
                        color="primary"
                        onClick={postGeneral}
                        className={classes.saveButton}
                        size="small"
                        >
                        save
                    </Button>
                </Typography>
            </Paper>
            <Paper className={classes.paper}>
                <Grid container
                    alignItems="center"
                    justify="space-between"
                    spacing={3}
                    >
                    <Grid item>
                        <Typography variant="h6" component="h3" gutterBottom>
                            Club Address
                        </Typography>
                    </Grid>
                </Grid>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={12} lg={6}>
                        <TextField
                            id="input-address-one"
                            label="Address One"
                            value={club.legal_address_one}
                            onChange={handleChange('legal_address_one')}
                            fullWidth
                            variant='outlined'
                            error={isError('legal_address_one')}
                            helperText={getErrorMessage('legal_address_one')}
                            />
                    </Grid>
                    <Grid item xs={12} sm={6} md={12} lg={6}>
                        <TextField
                            id="input-address-two"
                            label="Address Two"
                            value={club.legal_address_two}
                            onChange={handleChange('legal_address_two')}
                            fullWidth
                            variant='outlined'
                            error={isError('legal_address_two')}
                            helperText={getErrorMessage('legal_address_two')}
                            />
                    </Grid>
                    <Grid item xs={12} sm={5} md={12} lg={6}>
                        <TextField
                            id="input-city"
                            label="City"
                            value={club.legal_city}
                            onChange={handleChange('legal_city')}
                            fullWidth
                            variant='outlined'
                            error={isError('legal_city')}
                            helperText={getErrorMessage('legal_city')}
                            />
                    </Grid>
                    <Grid item xs={12} sm={4} md={12} lg={2}>
                        <TextField
                            id="input-state"
                            select
                            label="State"
                            value={club.legal_state}
                            onChange={handleChange('legal_state')}
                            fullWidth
                            variant='outlined'
                            error={isError('legal_state')}
                            helperText={getErrorMessage('legal_state')}
                            >
                            {States.map( state => {
                                return (
                                    <MenuItem key={state.abbreviation} value={state.abbreviation} >{state.name}</MenuItem>
                                )
                            })}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={3} md={12} lg={4}>
                        <TextField
                            id="input-zip"
                            label="ZIP"
                            value={club.legal_zip}
                            onChange={handleChange('legal_zip')}
                            fullWidth
                            variant='outlined'
                            error={isError('legal_zip')}
                            helperText={getErrorMessage('legal_zip')}
                            />
                    </Grid>
                </Grid>
                <Typography align="right" component="div" className={classes.saveButtonContainer}>
                    <Button
                        disabled={loading}
                        variant="contained"
                        color="primary"
                        onClick={postAddress}
                        className={classes.saveButton}
                        size="small"
                        >
                        save
                    </Button>
                </Typography>
            </Paper>
        </form>
    )
}
